import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { useArcStore } from '@/stores/arc-store';
import { useEmergencyStore } from '@/stores/emergency-store';
import { ExternalLink, X } from 'lucide-react';

const AUTO_PIP_PREF_KEY = 'asi-auto-pip-enabled';

export function isPipSupported(): boolean {
  return 'documentPictureInPicture' in window;
}

/** Unified alarm item shown in the PiP panel (arc_event or SOS/PANIC ticket). */
interface PipItem {
  id: string;
  label: string;
  sub: string;
  severity: 'CRITICAL' | 'ALARM';
  time: string;
  kind: 'arc' | 'alert';
}

function PipAlarmPanel({ items, hasNew, onOpenMain, onOpenEmergency, onEventClick }: {
  items: PipItem[];
  hasNew: boolean;
  onOpenMain: () => void;
  onOpenEmergency: () => void;
  onEventClick: (id: string, kind: 'arc' | 'alert') => void;
}) {
  const bg = hasNew ? '#b91c1c' : items.length > 0 ? '#dc2626' : '#1e293b';
  return (
    <div style={{ fontFamily: 'system-ui,sans-serif', background: '#0f172a', color: '#f1f5f9', height: '100vh', display: 'flex', flexDirection: 'column', userSelect: 'none' }}>
      <style>{'@keyframes pp{0%,100%{opacity:1}50%{opacity:.5}}'}</style>
      <div style={{ background: bg, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, transition: 'background .3s', animation: hasNew ? 'pp 1s ease-in-out 3' : 'none' }}>
        <span style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>{items.length > 0 ? items.length + ' alerta' + (items.length > 1 ? 's' : '') + ' activa' + (items.length > 1 ? 's' : '') : 'Sin alarmas criticas'}</span>
        <button onClick={onOpenEmergency} style={{ background: 'rgba(255,255,255,.13)', border: 'none', color: '#fff', borderRadius: 5, padding: '2px 7px', cursor: 'pointer', fontSize: 10, marginRight: 4 }}>Mapa</button>
        <button onClick={onOpenMain} style={{ background: 'rgba(255,255,255,.18)', border: 'none', color: '#fff', borderRadius: 5, padding: '2px 7px', cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>Cinta ↗</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {items.length === 0 ? (<div style={{ textAlign: 'center', color: '#475569', padding: '36px 16px', fontSize: 12 }}>Sin alarmas activas pendientes</div>)
        : items.map(item => (
          <button key={item.id} onClick={() => onEventClick(item.id, item.kind)} title="Clic para procesar"
            style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid #1e293b', padding: '9px 14px', display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', textAlign: 'left', color: '#f1f5f9', transition: 'background .15s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#1e293b'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <span style={{ color: item.severity === 'CRITICAL' ? '#ef4444' : '#f97316', fontSize: 14, marginTop: 2, flexShrink: 0 }}>●</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{item.sub} · {item.time}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
              <span style={{ fontSize: 9, background: item.kind === 'alert' ? '#1e3a5f' : '#7f1d1d', color: item.kind === 'alert' ? '#93c5fd' : '#fca5a5', borderRadius: 4, padding: '2px 5px' }}>{item.kind === 'alert' ? 'SOS/PANIC' : item.severity}</span>
              <span style={{ fontSize: 9, color: '#64748b' }}>→ procesar</span>
            </div>
          </button>
        ))}
      </div>
      <div style={{ padding: '5px 14px', borderTop: '1px solid #1e293b', fontSize: 10, color: '#475569', textAlign: 'center' }}>ASI Security · Toca un evento para atenderlo</div>
    </div>
  );
}

async function openPipWindow(w = 360, h = 500): Promise<Window | null> {
  if (!isPipSupported()) return null;
  try {
    // @ts-expect-error Chrome 116+
    const ex: Window | undefined = window.documentPictureInPicture?.window;
    if (ex && !ex.closed) return ex;
    // @ts-expect-error
    return await window.documentPictureInPicture.requestWindow({ width: w, height: h });
  } catch { return null; }
}

export function useAlarmPip() {
  const [isOpen, setIsOpen] = useState(false);
  const pipRef = useRef<Window | null>(null);
  const rootRef = useRef<ReturnType<typeof ReactDOM.createRoot> | null>(null);
  const arcEvents = useArcStore(s => s.events);
  const activeAlerts = useEmergencyStore(s => s.activeAlerts);
  const prevCount = useRef(0);
  const [hasNew, setHasNew] = useState(false);
  // Items dismissed by clicking — removed optimistically without waiting for Realtime.
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  /** Build unified PipItem list from arc_events + SOS/PANIC alerts */
  const buildItems = useCallback((evts: typeof arcEvents, alerts: typeof activeAlerts, dismissed: Set<string>): PipItem[] => {
    const arcItems: PipItem[] = evts
      .filter(e => !e.acknowledged_at && (e.severity === 'CRITICAL' || e.severity === 'ALARM') && !dismissed.has(e.id))
      .slice(0, 10)
      .map(e => ({
        id: e.id,
        label: e.site_name ?? 'Panel',
        sub: e.zone_name ?? ('CID ' + e.cid_code),
        severity: e.severity as 'CRITICAL' | 'ALARM',
        time: new Date(e.occurred_at ?? e.received_at).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
        kind: 'arc' as const,
      }));
    const alertItems: PipItem[] = alerts
      .filter(a => (a.status === 'PENDING' || a.status === 'ASSIGNED') && !dismissed.has(a.id))
      .slice(0, 5)
      .map(a => ({
        id: a.id,
        label: a.client_name ?? a.account_name ?? a.type,
        sub: a.type ?? 'SOS',
        severity: 'CRITICAL' as const,
        time: new Date(a.created_at).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
        kind: 'alert' as const,
      }));
    return [...alertItems, ...arcItems];
  }, []);

  const onEventClick = useCallback((id: string, kind: 'arc' | 'alert') => {
    // Dismiss optimistically so the PiP removes it immediately on click.
    setDismissedIds(prev => new Set([...prev, id]));
    window.focus();
    if (kind === 'alert') navigate(`/emergency`);
    else navigate(`/arc/events?event=${id}`);
  }, [navigate]);

  const renderPip = useCallback((evts: typeof arcEvents, alerts: typeof activeAlerts, dismissed: Set<string>, flag: boolean) => {
    if (!rootRef.current || !pipRef.current?.document) return;
    const items = buildItems(evts, alerts, dismissed);
    rootRef.current.render(
      <PipAlarmPanel items={items} hasNew={flag}
        onOpenMain={() => { window.focus(); navigate('/arc/events'); }}
        onOpenEmergency={() => { window.focus(); navigate('/emergency'); }}
        onEventClick={onEventClick}
      />
    );
  }, [navigate, onEventClick, buildItems]);

  const mountPip = useCallback(async (auto = false) => {
    const pip = await openPipWindow();
    if (!pip) { if (!auto) alert('Usa Chrome 116+ o Edge 116+.'); return; }
    if (!pip.document.getElementById('pip-root')) {
      pip.document.head.appendChild(Object.assign(pip.document.createElement('style'), { textContent: '*{box-sizing:border-box;margin:0;padding:0}' }));
      const el = Object.assign(pip.document.createElement('div'), { id: 'pip-root' });
      el.style.cssText = 'height:100vh;overflow:hidden;';
      pip.document.body.style.margin = '0';
      pip.document.body.appendChild(el);
      rootRef.current = ReactDOM.createRoot(el);
    }
    pipRef.current = pip;
    renderPip(arcEvents, activeAlerts, dismissedIds, false);
    setIsOpen(true);
    localStorage.setItem(AUTO_PIP_PREF_KEY, '1');
    pip.addEventListener('pagehide', () => {
      try { rootRef.current?.unmount(); } catch { /* noop */ }
      rootRef.current = null; pipRef.current = null; setIsOpen(false);
    }, { once: true });
  }, [arcEvents, activeAlerts, dismissedIds, renderPip]);

  const openPip = useCallback(() => mountPip(false), [mountPip]);
  const closePip = useCallback(() => { pipRef.current?.close(); localStorage.removeItem(AUTO_PIP_PREF_KEY); }, []);

  useEffect(() => {
    const items = buildItems(arcEvents, activeAlerts, dismissedIds);
    const count = items.length;
    const isNew = count > prevCount.current;
    if (isNew) {
      setHasNew(true);
      if (!isOpen && localStorage.getItem(AUTO_PIP_PREF_KEY) === '1') mountPip(true).catch(() => null);
      setTimeout(() => setHasNew(false), 3500);
    }
    prevCount.current = count;
    if (isOpen) renderPip(arcEvents, activeAlerts, dismissedIds, isNew);
  }, [arcEvents, activeAlerts, dismissedIds, isOpen, mountPip, renderPip, buildItems]);

  return { isOpen, openPip, closePip };
}

export function PipAlarmButton() {
  const { isOpen, openPip, closePip } = useAlarmPip();
  const location = useLocation();
  // Hook always runs (enables global auto-open), button only visible on /arc/events.
  if (!isPipSupported() || !location.pathname.startsWith('/arc/events')) return null;
  return (
    <button onClick={isOpen ? closePip : openPip} title={isOpen ? 'Cerrar ventana flotante' : 'Modo flotante — Chrome 116+'}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${isOpen ? 'bg-red-600/10 border-red-500/40 text-red-400 hover:bg-red-600/20' : 'bg-muted border-border text-muted-foreground hover:text-foreground hover:bg-muted/80'}`}>
      {isOpen ? <><X className="w-4 h-4" /> Cerrar flotante</> : <><ExternalLink className="w-4 h-4" /> Modo flotante</>}
    </button>
  );
}
